import React from 'react';
import { BIZ_TYPES, INTAKE_SOURCES, IntakeForm } from '../../types/intake';

/**
 * 사업 접수서 칸 1~8 — 「프로젝트 추가」 확인 단계와 검토 탭이 같이 쓴다(2026-10-22).
 * 빈 칸은 노란 테두리 + 「확인 필요」 — AI 가 원문에서 못 찾았거나 아직 안 채운 것.
 */
type TextKey = {
  [K in keyof IntakeForm]: IntakeForm[K] extends string | null ? K : never;
}[keyof IntakeForm];

const base = 'w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500';
/** 빈 칸 표시 — 선택 칸(optional)은 비어도 노랗지 않다 */
const cls = (v: string | null | undefined, optional = false) => `${base} ${optional || (v && v.trim()) ? 'border-slate-300' : 'border-amber-300 bg-amber-50/40'}`;

/** 컴포넌트 밖에 둔다 — 안에서 만들면 글자 칠 때마다 다시 붙어 입력 칸이 포커스를 잃는다 */
const Section: React.FC<{ no: number; title: string; children: React.ReactNode }> = ({ no, title, children }) => (
  <fieldset className="border-t border-slate-100 pt-3">
    <legend className="text-sm font-semibold text-slate-900 pr-2"><span className="text-indigo-600">{no}</span> {title}</legend>
    <div className="mt-2 grid gap-2.5 sm:grid-cols-2">{children}</div>
  </fieldset>
);

export const IntakeFields: React.FC<{
  value: IntakeForm;
  onChange: (v: IntakeForm) => void;
  /** 공고문이 없는 사업이면 5번(자격 · 제출)을 접는다 */
  showEligibility?: boolean;
}> = ({ value, onChange, showEligibility = true }) => {
  const set = (k: TextKey, v: string) => onChange({ ...value, [k]: v === '' ? null : v });
  const text = (k: TextKey, label: string, opts: { long?: boolean; required?: boolean; placeholder?: string; span?: boolean; optional?: boolean } = {}) => (
    <label className={`block text-xs text-slate-500 ${opts.span ? 'sm:col-span-2' : ''}`}>
      {label}{opts.required && <span className="text-red-500"> *</span>}
      {opts.long ? (
        <textarea value={(value[k] as string | null) ?? ''} onChange={(e) => set(k, e.target.value)} rows={3}
          placeholder={opts.placeholder ?? '확인 필요'} className={`mt-1 resize-y ${cls(value[k] as string | null, opts.optional)}`} />
      ) : (
        <input value={(value[k] as string | null) ?? ''} onChange={(e) => set(k, e.target.value)}
          placeholder={opts.placeholder ?? '확인 필요'} className={`mt-1 ${cls(value[k] as string | null, opts.optional)}`} />
      )}
    </label>
  );
  const lines = (k: 'documents' | 'unknowns', label: string, placeholder: string) => (
    <label className="block text-xs text-slate-500 sm:col-span-2">{label} <span className="text-slate-400">— 한 줄에 하나</span>
      <textarea value={value[k].join('\n')} rows={Math.max(2, Math.min(6, value[k].length + 1))} placeholder={placeholder}
        onChange={(e) => onChange({ ...value, [k]: e.target.value.split('\n').map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1) })}
        className={`mt-1 resize-y ${base} border-slate-300`} />
    </label>
  );

  return (
    <div className="space-y-4">
      <Section no={1} title="접수">
        <label className="block text-xs text-slate-500">출처
          <select value={value.source ?? ''} onChange={(e) => onChange({ ...value, source: (e.target.value || null) as IntakeForm['source'] })} className={`mt-1 ${cls(value.source)}`}>
            <option value="">확인 필요</option>
            {INTAKE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {text('receivedAt', '접수일')}
          {text('receivedBy', '접수자')}
        </div>
      </Section>
      <Section no={2} title="발주처">
        {text('clientOrg', '기관 · 회사', { required: true })}
        {text('clientDept', '부서')}
        {text('contactName', '담당자')}
        {text('contactPhone', '연락처')}
        {text('contactEmail', '이메일')}
      </Section>
      <Section no={3} title="사업">
        {text('bizName', '사업명', { required: true, span: true })}
        {text('noticeNo', '공고번호', { placeholder: '공고 없으면 비움', optional: true })}
        <label className="block text-xs text-slate-500">유형
          <select value={value.bizType ?? ''} onChange={(e) => onChange({ ...value, bizType: (e.target.value || null) as IntakeForm['bizType'] })} className={`mt-1 ${cls(value.bizType)}`}>
            <option value="">확인 필요</option>
            {BIZ_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {text('period', '기간')}
        {text('budget', '추정가격 · 예산', { placeholder: '원문 그대로 (VAT 포함/별도)' })}
        {text('dueAt', '제출 마감', { placeholder: 'YYYY-MM-DD HH:mm' })}
        {text('submitMethod', '제출 방법', { long: true, span: true })}
      </Section>
      <Section no={4} title="요청 내용">
        {text('background', '배경 · 목적', { long: true, span: true })}
        {text('scope', '과업 범위', { long: true, span: true })}
        {text('deliverables', '산출물', { long: true, span: true })}
      </Section>
      {showEligibility && (
        <Section no={5} title="자격 · 제출 (공고문이 있을 때)">
          {text('qualification', '참가 자격', { long: true, placeholder: '공고 없으면 비움', optional: true })}
          {text('trackRecord', '실적 요건', { long: true, placeholder: '공고 없으면 비움', optional: true })}
          {text('guarantee', '보증', { placeholder: '공고 없으면 비움', optional: true })}
          {lines('documents', '제출 서류', '사업자등록증')}
        </Section>
      )}
      <Section no={7} title="확인 필요">
        {lines('unknowns', 'AI 가 원문에서 못 찾은 것 · 물어볼 것', '예산')}
      </Section>
      <Section no={8} title="접수자 의견">
        {text('memo', '메모', { long: true, placeholder: '검토 메모로 들어가고 제안서 AI 초안이 읽습니다', optional: true })}
        {text('next', '다음 할 일', { long: true, placeholder: '예: 담당자에게 예산 확인', optional: true })}
      </Section>
    </div>
  );
};
